import { PaginationSchema, PriceSchema } from '@features/state';
import { z } from 'zod';

export const CalendarAvailabilitySchema = z.object({
  date: z.string(),
  minimum_stay: z.number(),
  maximum_stay: z.number().nullable(),
  type: z.enum(['available', 'unavailable', 'check_in_closed', 'hotel_closed']),
  price: z
    .object({
      amount: z.number(),
      currency_code: z.string(),
    })
    .nullable(),
});

export const CalendarAvailabilityResponseSchema = z.array(CalendarAvailabilitySchema);

const RoomExtraPricingSchema = z.union([
  z.literal('per_person_per_stay'),
  z.literal('per_person_per_night'),
  z.literal('per_stay'),
  z.literal('per_night'),
]);

const RoomExtraPerStaySchema = z.object({
  type: z.literal('per_stay'),
});

const RoomExtraPerDateSchema = z.object({
  type: z.literal('per_date'),
  options: z.array(
    z.object({
      startDate: z.string(),
      timezone: z.string(),
      maxQuantity: z.number().nullable(),
      metadata: z.string().nullable(),
    })
  ),
});

const RoomExtraPerDateTimeSchema = z.object({
  type: z.literal('per_date_time'),
  options: z.array(
    z.object({
      date: z.string(),
      timezone: z.string(),
      maxQuantity: z.number().nullable(),
      time: z.string(),
      metadata: z.string(),
    })
  ),
});

const RoomExtraPerDateRangeSchema = z.object({
  type: z.literal('per_date_range'),
  options: z.array(
    z.object({
      startDate: z.string(),
      endDate: z.string(),
      timezone: z.string(),
      maxQuantity: z.number().nullable(),
      metadata: z.string(),
    })
  ),
});

const RoomExtraPerDateTimeRangeSchema = z.object({
  type: z.literal('per_date_time_range'),
  options: z.array(
    z.object({
      startDate: z.string(),
      startTime: z.string(),
      endDate: z.string(),
      endTime: z.string(),
      timezone: z.string(),
      maxQuantity: z.number().nullable(),
      metadata: z.string().nullable(),
      name: z.string().nullable(),
    })
  ),
});

const RoomExtraPerTimeRangeSchema = z.object({
  type: z.literal('per_time_range'),
  options: z.array(
    z.object({
      startTime: z.string(),
      endTime: z.string(),
      timezone: z.string(),
      maxQuantity: z.number().nullable(),
      metadata: z.string(),
    })
  ),
});

export const RoomExtraOptions = z.discriminatedUnion('type', [
  RoomExtraPerStaySchema,
  RoomExtraPerDateSchema,
  RoomExtraPerDateTimeSchema,
  RoomExtraPerDateRangeSchema,
  RoomExtraPerDateTimeRangeSchema,
  RoomExtraPerTimeRangeSchema,
]);

const ExtraPriceSchema = z.object({
  amount: z.number(),
  currencyCode: z.string(),
});

export const RoomExtraSchema = z
  .object({
    id: z.number().optional(),
    name: z.string(),
    description: z.string().nullable(),
    category: z.object({ name: z.string() }).nullable(),
    price: ExtraPriceSchema.nullable(),
    extra: z.any(),
    pricingType: RoomExtraPricingSchema.nullable(),
    image: z.union([
      z.array(z.object({ url: z.string() })).nullable(),
      z.object({ url: z.string() }).nullable(),
    ]),
    maxQuantity: z.number().nullable(),
    policies: z.string().nullable(),
    quantityLabel: z.string().nullable(),
    metadata: z.string(),
    pricePerNight: ExtraPriceSchema.nullable(),
  })
  .and(RoomExtraOptions);

export const RoomExtrasSchema = RoomExtraSchema.array();

export const RoomAndRatePageSettingsSchema = z.object({
  hero_background_image: z
    .object({
      external_key: z.string(),
      url: z.string(),
    })
    .nullable(),
  id: z.number(),
  property: z.object({
    id: z.number(),
  }),
  content: z.string(),
  slug: z.string(),
  title: z.string(),
});

export const StaticRoomSchema = z.object({
  room_id: z.string(),
  title: z.string(),
  image_url: z.string().nullable(),
  max_occupancy: z.number().nullable(),
  max_adults: z.number().nullable(),
  max_children: z.number().nullable(),
});

export const StaticRoomsSchema = z.object({
  data: z.array(StaticRoomSchema),
});

export const AvailableRoomSchema = z.object({
  id: z.string(),
  display_urgency_messaging: z.boolean(),
  quantity: z.number(),
  price_per_night: PriceSchema.nullable(),
  price_per_stay: PriceSchema.nullable(),
});

export const AvailableRoomsSchema = PaginationSchema(AvailableRoomSchema);

export const StaticRoomByIdSchema = z.object({
  additional_images: z.array(z.object({ image_url: z.string() })).nullable(),
  amenities: z.array(z.string()).nullable(),
  beds: z.object({
    total: z.number(),
    types: z.array(z.object({ name: z.string(), quantity: z.number() })).nullable(),
  }),
  description: z.string(),
  image_url: z.string().nullable(),
  max_adults: z.number().nullable(),
  max_children: z.number().nullable(),
  max_occupancy: z.number(),
  min_adults: z.number().nullable(),
  min_children: z.number().nullable(),
  room_id: z.string(),
  title: z.string(),
  overriddenData: z
    .object({
      name: z.string().nullable(),
      images: z.array(z.string()).nullable(),
      max_adults: z.number().nullable(),
      max_children: z.number().nullable(),
      description: z.string().nullable(),
      max_occupancy: z.number().nullable(),
      min_adults: z.number().nullable(),
      min_children: z.number().nullable(),
    })
    .nullish(),
});

const AvailableRoomByIdRate = z.object({
  description: z.string().nullable(),
  display_urgency_messaging: z.boolean(),
  from_price: PriceSchema,
  id: z.string(),
  image_url: z.string().nullable(),
  name: z.string(),
  prices_per_night: z.array(z.number()).nullable(),
  quantity: z.number(),
  room_upgrade: z
    .object({
      amenities: z.array(z.string()).nullable(),
      description: z.string().nullable(),
      display_urgency_messaging: z.boolean(),
      id: z.string(),
      image_url: z.string().nullable(),
      max_adults: z.number().nullable(),
      max_children: z.number().nullable(),
      max_occupancy: z.number(),
      min_adults: z.number().nullable(),
      min_children: z.number().nullable(),
      name: z.string(),
      price_difference: PriceSchema,
      price_per_night: PriceSchema,
      price_per_stay: PriceSchema,
      quantity: z.number(),
    })
    .nullable(),
  terms: z.string().nullable(),
});

export const AvailableRoomByIdSchema = z
  .object({
    display_urgency_messaging: z.boolean(),
    id: z.string(),
    price_per_night: PriceSchema,
    price_per_stay: PriceSchema,
    quantity: z.number(),
    rates: z.array(AvailableRoomByIdRate),
  })
  .nullable();
